// src/services/staffService.ts
import { supabase } from '../supabase/config';
import { withDirectoryFields } from '../lib/directoryProxy';

// Basic staff member interface
export interface StaffMember {
  id: string;
  staff_id?: string; // Legacy field, may not be in database
  staff_code?: string; // Manual 3-letter staff code (takes priority)
  initial?: string; // Auto-generated 3-letter teacher code
  name: string;
  email?: string;
  department?: string;
  role?: 'admin' | 'principal' | 'assistant_principal' | 'head_of_department' | 'curriculum_leader' | 'teacher';
  curriculum_area?: string; // Changed from curriculumArea to match PostgreSQL naming
  employment_type?: string; // Changed from employmentType to match PostgreSQL naming
  fte?: number;
  base_minutes_per_fortnight?: number; // Base teaching minutes per fortnight
  status: 'active' | 'inactive' | 'pending' | 'suspended' | 'leave'; // Changed isActive to status
  permissions?: string[];
  semester1?: any; // Semester 1 data (allowances, load calculations)
  semester2?: any; // Semester 2 data
  both?: any; // Allowances that apply to both semesters
  created_at: Date; // Changed from createdAt to match PostgreSQL naming
  updated_at: Date; // Changed from lastUpdatedAt to match PostgreSQL naming
}

/**
 * Service for managing staff members in Supabase
 */
export const staffService = {
  /**
   * Save a staff member to Supabase
   * @param staff The staff member to save
   */
  async saveStaffMember(staff: StaffMember): Promise<void> {
    try {
      const { error } = await supabase
        .from('staff')
        .upsert({
          id: staff.id,
          staff_id: staff.staff_id,
          name: staff.name,
          email: staff.email,
          department: staff.department,
          role: staff.role,
          curriculum_area: staff.curriculum_area,
          employment_type: staff.employment_type,
          fte: staff.fte,
          status: staff.status,
          permissions: staff.permissions,
          updated_at: new Date().toISOString()
        });

      if (error) {
        console.error('Error saving staff member:', error);
        throw error;
      }
    } catch (error) {
      console.error('Error saving staff member:', error);
      throw error;
    }
  },

  /**
   * Get all staff members
   * @returns Array of staff members
   */
  async getAllStaff(): Promise<StaffMember[]> {
    try {
      const { data, error } = await supabase
        .from('staff')
        .select('*')
        .order('name');

      if (error) {
        console.error('Error getting all staff:', error);
        throw error;
      }

      // pit#274: name/email/department from directory when VITE_DIRECTORY_READS=on.
      const rows = await withDirectoryFields(data ?? []);
      return rows.map(item => ({
        id: item.id,
        staff_id: item.staff_id,
        staff_code: item.staff_code,
        initial: item.initial,
        name: item.name,
        email: item.email,
        department: item.department,
        role: item.role,
        curriculum_area: item.curriculum_area,
        employment_type: item.employment_type,
        fte: item.fte,
        base_minutes_per_fortnight: item.base_minutes_per_fortnight,
        status: item.status,
        permissions: item.permissions || [],
        semester1: item.semester1,
        semester2: item.semester2,
        both: item.both,
        created_at: new Date(item.created_at),
        updated_at: new Date(item.updated_at)
      }));
    } catch (error) {
      console.error('Error getting all staff:', error);
      return [];
    }
  },

  /**
   * Get a staff member by ID
   * @param id The staff member ID
   * @returns Staff member or null if not found
   */
  async getStaffById(id: string): Promise<StaffMember | null> {
    try {
      const { data: row, error } = await supabase
        .from('staff')
        .select('*')
        .eq('id', id)
        .single();

      if (error) {
        if (error.code === 'PGRST116') { // Not found
          return null;
        }
        console.error('Error getting staff by ID:', error);
        throw error;
      }

      // pit#274: name/email/department from directory when VITE_DIRECTORY_READS=on.
      const [data] = await withDirectoryFields([row]);
      return {
        id: data.id,
        staff_id: data.staff_id,
        name: data.name,
        email: data.email,
        department: data.department,
        role: data.role,
        curriculum_area: data.curriculum_area,
        employment_type: data.employment_type,
        fte: data.fte,
        status: data.status,
        permissions: data.permissions || [],
        created_at: new Date(data.created_at),
        updated_at: new Date(data.updated_at)
      };
    } catch (error) {
      console.error('Error getting staff by ID:', error);
      return null;
    }
  },

  /**
   * Get a staff member by email
   * @param email The staff member email
   * @returns Staff member or null if not found
   */
  async getStaffByEmail(email: string): Promise<StaffMember | null> {
    try {
      const { data, error } = await supabase
        .from('staff')
        .select('*')
        .eq('email', email)
        .single();

      if (error) {
        if (error.code === 'PGRST116') { // Not found
          return null;
        }
        console.error('Error getting staff by email:', error);
        throw error;
      }

      return {
        id: data.id,
        staff_id: data.staff_id,
        name: data.name,
        email: data.email,
        department: data.department,
        role: data.role,
        curriculum_area: data.curriculum_area,
        employment_type: data.employment_type,
        fte: data.fte,
        status: data.status,
        permissions: data.permissions || [],
        created_at: new Date(data.created_at),
        updated_at: new Date(data.updated_at)
      };
    } catch (error) {
      console.error('Error getting staff by email:', error);
      return null;
    }
  },

  /**
   * Get staff members by department
   * @param department The department name
   * @returns Array of staff members
   */
  async getStaffByDepartment(department: string): Promise<StaffMember[]> {
    try {
      const { data, error } = await supabase
        .from('staff')
        .select('*')
        .eq('department', department)
        .order('name');

      if (error) {
        console.error('Error getting staff by department:', error);
        throw error;
      }

      return data.map(item => ({
        id: item.id,
        staff_id: item.staff_id,
        name: item.name,
        email: item.email,
        department: item.department,
        role: item.role,
        curriculum_area: item.curriculum_area,
        employment_type: item.employment_type,
        fte: item.fte,
        status: item.status,
        permissions: item.permissions || [],
        created_at: new Date(item.created_at),
        updated_at: new Date(item.updated_at)
      }));
    } catch (error) {
      console.error('Error getting staff by department:', error);
      return [];
    }
  },

  /**
   * Get staff members by curriculum area
   * @param curriculumArea The curriculum area
   * @returns Array of staff members
   */
  async getStaffByCurriculumArea(curriculumArea: string): Promise<StaffMember[]> {
    try {
      const { data, error } = await supabase
        .from('staff')
        .select('*')
        .eq('curriculum_area', curriculumArea)
        .order('name');

      if (error) {
        console.error('Error getting staff by curriculum area:', error);
        throw error;
      }

      return data.map(item => ({
        id: item.id,
        staff_id: item.staff_id,
        name: item.name,
        email: item.email,
        department: item.department,
        role: item.role,
        curriculum_area: item.curriculum_area,
        employment_type: item.employment_type,
        fte: item.fte,
        status: item.status,
        permissions: item.permissions || [],
        created_at: new Date(item.created_at),
        updated_at: new Date(item.updated_at)
      }));
    } catch (error) {
      console.error('Error getting staff by curriculum area:', error);
      return [];
    }
  },

  /**
   * Get active staff members only
   * @returns Array of active staff members
   */
  async getActiveStaff(): Promise<StaffMember[]> {
    try {
      const { data, error } = await supabase
        .from('staff')
        .select('*')
        .eq('status', 'active')
        .order('name');

      if (error) {
        console.error('Error getting active staff:', error);
        throw error;
      }

      return data.map(item => ({
        id: item.id,
        staff_id: item.staff_id,
        name: item.name,
        email: item.email,
        department: item.department,
        role: item.role,
        curriculum_area: item.curriculum_area,
        employment_type: item.employment_type,
        fte: item.fte,
        status: item.status,
        permissions: item.permissions || [],
        created_at: new Date(item.created_at),
        updated_at: new Date(item.updated_at)
      }));
    } catch (error) {
      console.error('Error getting active staff:', error);
      return [];
    }
  },

  /**
   * Update a staff member
   * @param id The staff member ID
   * @param updates The fields to update
   */
  async updateStaffMember(id: string, updates: Partial<StaffMember>): Promise<void> {
    try {
      const { error } = await supabase
        .from('staff')
        .update({
          ...updates,
          updated_at: new Date().toISOString()
        })
        .eq('id', id);

      if (error) {
        console.error('Error updating staff member:', error);
        throw error;
      }
    } catch (error) {
      console.error('Error updating staff member:', error);
      throw error;
    }
  },

  /**
   * Delete a staff member
   * @param id The staff member ID
   */
  async deleteStaffMember(id: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('staff')
        .delete()
        .eq('id', id);

      if (error) {
        console.error('Error deleting staff member:', error);
        throw error;
      }
    } catch (error) {
      console.error('Error deleting staff member:', error);
      throw error;
    }
  },

  /**
   * Bulk save staff members
   * @param staffMembers Array of staff members to save
   */
  async bulkSaveStaff(staffMembers: StaffMember[]): Promise<void> {
    try {
      const staffData = staffMembers.map(staff => ({
        id: staff.id,
        staff_id: staff.staff_id,
        name: staff.name,
        email: staff.email,
        department: staff.department,
        role: staff.role,
        curriculum_area: staff.curriculum_area,
        employment_type: staff.employment_type,
        fte: staff.fte,
        status: staff.status,
        permissions: staff.permissions,
        updated_at: new Date().toISOString()
      }));

      const { error } = await supabase
        .from('staff')
        .upsert(staffData);

      if (error) {
        console.error('Error bulk saving staff:', error);
        throw error;
      }
    } catch (error) {
      console.error('Error bulk saving staff:', error);
      throw error;
    }
  },

  /**
   * Create staff records from teaching intentions
   * @param intentions Array of teaching intentions to create staff from
   * @returns Number of staff records created
   */
  async createStaffFromIntentions(intentions: any[]): Promise<number> {
    try {
      const staffToCreate = intentions
        .filter(intention => intention.intentionToReturn === 'yes')
        .map(intention => ({
          id: `staff-${intention.staffId}-${Date.now()}`,
          staff_id: intention.staffId,
          name: intention.staffName,
          email: intention.email,
          department: intention.department,
          employment_type: intention.desiredEmploymentStatus || intention.currentEmploymentType,
          fte: intention.targetFTE || intention.currentFTE,
          status: 'active' as const,
          permissions: []
        }));

      if (staffToCreate.length === 0) {
        return 0;
      }

      await this.bulkSaveStaff(staffToCreate);
      return staffToCreate.length;
    } catch (error) {
      console.error('Error creating staff from intentions:', error);
      throw error;
    }
  }
};